<script setup lang="ts">
import { computed, ref, watch } from "vue";
import Modal from "@/ui/components/modal.vue";
import useToast from "@/ui/composables/useToast";
import { useBasePriceStore } from "@/modules/base_price/store";
import PlanPreviousYearsChart from "@/modules/base_price/components/PlanPreviousYearsChart.vue";
import BasePricePreviousYearDistributionDTO = Modules.BasePrice.Data.PreviousYear.BasePricePreviousYearDistributionDTO;

/**
 * "Derive distribution from previous years": loads what earlier years looked
 * like for the node or screen being edited, as the start of shaping this
 * plan's months after them. Fetched every time it opens, since the selection
 * may have moved in between.
 */
const props = defineProps<{
	/** The node or screen the tab is editing, for the dialog's subtitle. */
	targetName: string;
}>();

const visible = defineModel<boolean>("visible", { default: false });

const store = useBasePriceStore();

/** The years the figures are for: the two before the plan's (see PreviousYearDistributionService). */
const currentYear = computed(() => (store.plan?.year ?? 0) - 1);
const previousYear = computed(() => (store.plan?.year ?? 0) - 2);

const loading = ref(false);
const distribution = ref<BasePricePreviousYearDistributionDTO | null>(null);

/** The twelve months, normalized out of the keyed-object-or-array shape a Collection serializes to. */
const months = computed(() =>
	distribution.value?.months ? Object.values(distribution.value.months) : []
);

const load = async () => {
	loading.value = true;
	distribution.value = null;

	const response = await store.fetchPreviousYearsDistribution();

	loading.value = false;

	if (response?.isErr()) {
		useToast().error(response.error.message);

		return;
	}

	distribution.value = response?.isOk() ? response.value : null;
};

watch(visible, (open) => open && load());

const emit = defineEmits<{
	/** The twelve months' shares to shape the distribution after, January first. */
	apply: [shares: number[]];
}>();

/**
 * The year before the plan is only a pattern of its own once every month has passed: its
 * shares are on a whole-year scale, so they reach 100% only then.
 */
const currentYearComplete = computed(
	() =>
		months.value.length > 0 &&
		Math.abs(
			months.value.reduce(
				(sum, month) => sum + (month.current_year_share ?? 0),
				0
			) - 100
		) < 0.01
);

/** Two years before the plan or the average — always complete — or the year before the plan once it is. */
const apply = (
	key: "current_year_share" | "previous_year_share" | "average_share"
) => {
	emit(
		"apply",
		months.value.map((month) => month[key] ?? 0)
	);
	visible.value = false;
};
</script>

<template>
	<modal
		v-model:visible="visible"
		title="Derive distribution from previous years"
		icon="fa-light fa-clock-rotate-left"
		width="56rem"
		:persistent="false"
	>
		<div v-if="loading" class="d-flex flex-column align-center ga-3 py-8">
			<v-progress-circular indeterminate color="primary" size="40" />
			<span class="text-caption text-medium-emphasis"
				>Loading previous years…</span
			>
		</div>

		<div
			v-else-if="!months.length"
			class="text-body-2 text-medium-emphasis text-center py-8"
		>
			No previous-year data found for {{ props.targetName }}.
		</div>

		<template v-else>
			<!-- Whose years these are: the node or screen the tab was editing when it opened. -->
			<div class="d-flex align-baseline ga-2 mb-1">
				<span class="text-subtitle-1 font-weight-bold">{{
					props.targetName
				}}</span>
				<span v-if="store.editTarget" class="text-caption text-medium-emphasis">
					{{ store.editTarget.kindLabel }}
				</span>
			</div>

			<p class="text-caption text-medium-emphasis mb-3">
				Each line shows a month's share of its year, where an even month counts
				as 1/12. {{ currentYear }} only includes the months that have fully
				passed, so it adds up to less than 100% until the year is complete. The
				average is the mean of {{ currentYear }} and {{ previousYear }} per month;
				for a month that has not passed yet it takes {{ previousYear }}'s figure,
				adjusted by how {{ currentYear }} is trending against
				{{ previousYear }} over the months that have passed.
			</p>

			<plan-previous-years-chart
				:months="months"
				:current-year="currentYear"
				:previous-year="previousYear"
			/>
		</template>

		<template v-slot:actions>
			<div class="mx-5">Apply:</div>
			<v-btn
				class="flex-1-1"
				variant="tonal"
				:disabled="loading || !currentYearComplete"
				@click="apply('current_year_share')"
			>
				{{ currentYear }}
			</v-btn>
			<v-btn
				class="flex-1-1"
				variant="tonal"
				:disabled="loading || !months.length"
				@click="apply('previous_year_share')"
			>
				{{ previousYear }}
			</v-btn>
			<v-btn
				class="flex-1-1"
				variant="tonal"
				:disabled="loading || !months.length"
				@click="apply('average_share')"
			>
				Average
			</v-btn>
		</template>
	</modal>
</template>
